import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { languages } from '../data/languages';

const ThankYouPage: React.FC = () => {
  const { langId } = useParams<{ langId: string }>();
  const navigate = useNavigate();
  const [language, setLanguage] = useState(languages.find(lang => lang.id === langId));

  useEffect(() => {
    // Vérifier si la langue existe
    if (!language) {
      navigate('/');
    }
  }, [language, navigate]);

  if (!language) {
    return null;
  }

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 10
      }
    }
  };

  const floatingBubbleVariants = {
    animate: (i: number) => ({
      y: [0, -15, 0],
      x: [0, i % 2 === 0 ? 10 : -10, 0],
      transition: {
        duration: 3 + i * 0.5,
        repeat: Infinity,
        ease: "easeInOut"
      }
    })
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center overflow-hidden text-white"
      style={{
        background: `linear-gradient(135deg, ${language.color}99, ${language.color}44, #001233)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      {/* Animated floating circles in background */}
      <div className="absolute inset-0 overflow-hidden">
        {Array.from({ length: 15 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              background: `${language.color}40`,
              width: Math.random() * 100 + 30,
              height: Math.random() * 100 + 30,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              filter: 'blur(8px)'
            }}
            custom={i}
            variants={floatingBubbleVariants}
            animate="animate"
          />
        ))}
      </div>

      <div className="container mx-auto px-4 py-20 relative z-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="max-w-xl mx-auto bg-white/10 backdrop-blur-md rounded-2xl p-8 md:p-12 border border-white/30 shadow-2xl"
        >
          <div className="text-center">
            <motion.div
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="mb-4 flex justify-center"
            >
              <span className="text-8xl inline-block">{language.flagEmoji}</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ color: language.color }}
            >
              {language.thankYou}
            </motion.h1>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="space-y-6 mb-8"
            >
              <motion.p variants={itemVariants} className="text-xl text-gray-100">
                Nous vous remercions d'avoir choisi {language.name} comme langue.
              </motion.p>
              
              <motion.p variants={itemVariants} className="text-md text-gray-200">
                Vous pouvez maintenant explorer notre application dans la langue de votre choix. Nous espérons que vous apprécierez l'expérience.
              </motion.p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.5 }}
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/')}
                className="px-8 py-3 rounded-full bg-white text-gray-900 font-medium shadow-lg transition duration-300"
              >
                Retour à l'accueil
              </motion.button>
            </motion.div>
          </div>
        </motion.div>

        {/* Appreciation phrases in language */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1 }}
          className="max-w-4xl mx-auto mt-16 grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {[
            {
              emoji: "💫",
              phrase: "Appréciation",
              description: "Nous apprécions votre intérêt pour notre plateforme multilingue."
            },
            {
              emoji: "🌟",
              phrase: "Excellence",
              description: "Nous nous efforçons d'offrir une expérience exceptionnelle dans toutes les langues."
            },
            {
              emoji: "🤝",
              phrase: "Connexion",
              description: "Les langues nous connectent et nous rapprochent à travers le monde."
            }
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 + index * 0.2, duration: 0.5 }}
              className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10"
              whileHover={{ 
                y: -5,
                boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.2)",
                borderColor: 'rgba(255, 255, 255, 0.3)'
              }}
            >
              <div className="text-3xl mb-3">{item.emoji}</div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: language.color }}>{item.phrase}</h3>
              <p className="text-sm text-gray-300">{item.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default ThankYouPage;
